import type { Event } from '@sentry/vue'
import * as Sentry from '@sentry/vue'

/** Intercom messenger frame bundle (e.g. frame.5.6.0.min.js) — not our code. */
const INTERCOM_FRAME_SCRIPT = /\/frame\.[\d.]+\.min\.js($|\?|#)/

/** WebKit: fetch/promises aborted when tab navigates away; not actionable. */
const BROWSING_CONTEXT_GOING_AWAY = /browsing context is going away/i

function isBrowsingContextGoingAwayEvent(event: Event): boolean {
  const exception = event.exception?.values?.[0]
  const value = exception?.value ?? ''
  const message = event.message ?? ''
  return (
    BROWSING_CONTEXT_GOING_AWAY.test(value) ||
    BROWSING_CONTEXT_GOING_AWAY.test(message)
  )
}

function isIntercomMessengerFailedFetchEvent(event: Event): boolean {
  const exception = event.exception?.values?.[0]
  const message = exception?.value ?? event.message ?? ''
  if (!message.includes('Failed to fetch')) {
    return false
  }

  const frames = exception?.stacktrace?.frames ?? []
  if (frames.length === 0) {
    return false
  }

  return frames.every((frame) => {
    const name = frame.filename ?? ''
    return name.includes('intercom') || INTERCOM_FRAME_SCRIPT.test(name)
  })
}

export default defineNuxtPlugin((nuxtApp) => {
  const {
    public: { sentry },
  } = useRuntimeConfig()

  if (!sentry.dsn) {
    return
  }

  Sentry.init({
    app: nuxtApp.vueApp,
    dsn: sentry.dsn,
    environment: sentry.environment,
    integrations: [
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
      }),
      Sentry.launchDarklyIntegration(),
    ],
    tracesSampleRate: 0.01,
    replaysOnErrorSampleRate: 0.5,
    replaysSessionSampleRate: 0,
    ignoreErrors: [
      'ResizeObserver loop completed with undelivered notifications',
      'Missing key id (kid)',
    ],

    beforeSend(event) {
      if (isBrowsingContextGoingAwayEvent(event)) {
        return null
      }
      if (isIntercomMessengerFailedFetchEvent(event)) {
        return null
      }
      return event
    },
  })
})
